/**
 * The mosaic of the calendar: doors in a random order, a few of them wider or taller.
 *
 * RANDOM, BUT FIXED PER ACTION. The seed is the action's id, so the arrangement is the same
 * on every visit, on every device and behind every cache: a participant who found door 7
 * bottom left yesterday finds it there today. A new action gets a new mosaic. A daily
 * reshuffle would be one line at the call site (the id plus the local date as seed), and it
 * would cost exactly that recognition.
 *
 * SMALL SIZE DIFFERENCES. Doors are one cell, two cells wide or two cells tall, never
 * larger. Which door gets the larger frame depends on its place in the shuffle, not on its
 * state, so the mosaic does not move when a door opens.
 *
 * NO HOLES. The grid runs with 3 columns on a phone and 6 from a mid width upwards
 * (field.css), or with the counts an action configures (D29), and `grid-auto-flow: dense`
 * packs the doors. The larger doors add cells until the total divides by every count, so
 * each layout can close as a rectangle. Whether they do depends
 * on the order: a tall door that comes up when the last row is the only free one opens a row
 * that nothing fills. So every candidate is packed here exactly as the browser packs it
 * (CSS Grid 8.5, dense), and a candidate with a gap is drawn again. The test replays the
 * packing independently for every size from 1 to 40 and many seeds.
 *
 * READING ORDER. The markup order is this order, so keyboard and screen reader follow the
 * mosaic as it is drawn; dense packing can pull a single door into a gap further up, which
 * is the one place where the two differ (accepted, docs/barrierefreiheit.md).
 */
/** The column counts of the calendar grid in field.css. The arrangement closes both. */
export const GRID_COLUMN_COUNTS = [3, 6];
/** Draws per arrangement. Measured: 24 doors close at the first draw for most seeds. */
const ATTEMPTS = 64;
export function arrangeFields(positions, seed, { layout = 'mosaic', columns = GRID_COLUMN_COUNTS, fillers = 0 } = {}) {
    const random = mulberry32(fnv1a(seed));
    const empty = Array.from({ length: Math.max(0, Math.floor(fillers)) }, (_, index) => -(index + 1));
    const order = shuffle([...positions, ...empty], random);
    const plain = order.map((position) => ({ position, size: 'single' }));
    // A larger door needs two columns; one column would leave nowhere to put it.
    if (layout !== 'mosaic' || columns.some((count) => count < 2)) {
        return plain;
    }
    const spans = spanCount(order.length, columns.reduce(leastCommonMultiple, 1));
    if (spans === 0) {
        return plain;
    }
    // The larger doors are drawn from the first two thirds, so single doors remain behind
    // them to close whatever gap the packing leaves.
    const window = Math.floor((order.length * 2) / 3);
    const wide = Math.ceil((spans * 2) / 3);
    for (let attempt = 0; attempt < ATTEMPTS; attempt += 1) {
        const slots = shuffle(Array.from({ length: window }, (_, index) => index), random).slice(0, spans);
        const sizeAt = new Map(slots.map((slot, rank) => [slot, rank < wide ? 'wide' : 'tall']));
        const candidate = order.map((position, index) => ({
            position,
            size: sizeAt.get(index) ?? 'single',
        }));
        if (columns.every((count) => packsWithoutGap(candidate, count))) {
            return candidate;
        }
    }
    // Every draw left a gap: the plain grid, which at worst leaves the last row short.
    return plain;
}
/**
 * How many doors get a larger frame. Each adds one cell, and the total has to reach a
 * multiple of every column count (6 for the built-in 3 and 6). Aimed at about a quarter of
 * the doors (24 doors: 6), never more than half; a calendar too small for that stays a plain
 * grid, and so do counts whose common multiple is out of reach (4, 5 and 7 need 140 cells).
 */
function spanCount(count, multiple) {
    const target = count / 4;
    let spans = (multiple - (count % multiple)) % multiple;
    while (spans + multiple <= count / 2 &&
        Math.abs(spans + multiple - target) <= Math.abs(spans - target)) {
        spans += multiple;
    }
    return spans <= count / 2 ? spans : 0;
}
function leastCommonMultiple(a, b) {
    const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
    return (a * b) / gcd(a, b);
}
/**
 * `grid-auto-flow: dense` as the browser runs it: each door goes to the first place, rows
 * from the top and columns from the left, where all of its cells are free. True when the
 * doors fill whole rows and nothing else.
 */
function packsWithoutGap(arranged, columns) {
    const taken = new Set();
    let rows = 0;
    for (const { size } of arranged) {
        const width = size === 'wide' ? 2 : 1;
        const height = size === 'tall' ? 2 : 1;
        const place = firstFit(taken, columns, width, height);
        place.cells.forEach((index) => taken.add(index));
        rows = Math.max(rows, place.row + height);
    }
    return rows * columns === taken.size;
}
/** The first free area of the given size, scanning rows, then columns. The grid grows down. */
function firstFit(taken, columns, width, height) {
    for (let row = 0;; row += 1) {
        for (let column = 0; column + width <= columns; column += 1) {
            const cells = Array.from({ length: width * height }, (_, index) => {
                const dy = Math.floor(index / width);
                return (row + dy) * columns + column + (index % width);
            });
            if (cells.every((index) => !taken.has(index))) {
                return { row, cells };
            }
        }
    }
}
/** Fisher-Yates on a copy. */
function shuffle(items, random) {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index -= 1) {
        const other = Math.floor(random() * (index + 1));
        const current = result[index];
        result[index] = result[other];
        result[other] = current;
    }
    return result;
}
/** 32-bit FNV-1a: turns the seed string into a number. */
function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (const character of text) {
        hash ^= character.codePointAt(0) ?? 0;
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
/** A small seeded generator. Not for anything secret: it only has to be repeatable. */
function mulberry32(seed) {
    let state = seed;
    return () => {
        // Wraps at 32 bits on purpose; the generator is defined on uint32 arithmetic.
        state = (state + 0x6d2b79f5) >>> 0;
        let value = Math.imul(state ^ (state >>> 15), 1 | state);
        value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
}
//# sourceMappingURL=arrange-fields.js.map